<template>
  <picker
    :id="id"
    label="color"
    :text="value"
    :skeleton="skeleton"
    class="picker--color"
    data-cy-color-picker
  >
    <div
      class="picker__label-container d-flex flex-wrap align-baseline justify-center"
    >
      <p class="picker__label text-big text-secondary mb-0-8">
        <span v-if="withLabel" class="label">{{ $t('label.color') }}</span>
        <span>{{ hoveredColor || selectColorLabel }}</span>
      </p>

      <p
        v-if="showThird"
        ref="thirdDimension"
        class="picker__label picker__label--third d-flex align-center text-extra-small text-md-small text-secondary mb-0"
      >
        <span>
          <span class="d-none d-md-inline">
            {{
              thirdDimensionLabel
                ? $t('product.material.label', { label: thirdDimensionLabel })
                : $t('product.material.title')
            }}&nbsp;</span
          ><span>{{ hoveredThird || thirdDimension?.label }}</span>
        </span>
        <icon name="info" class="ml-0-2" />
      </p>

      <transition name="fade-from-left">
        <div
          v-if="showIsLeavingSoon || showIsNew"
          ref="colorNotif"
          class="picker--color__notif d-md-none"
        >
          <p
            class="d-flex align-center text-secondary text-color-yellow text-extra-small lh-1 mb-0-6"
          >
            <span
              >{{
                $i18n.t(
                  `product.color.${showIsNew ? 'new' : 'leaving_soon'}.title`
                )
              }}
            </span>
            <icon name="info" class="ml-0-2" />
          </p>
        </div>
      </transition>
    </div>
    <div
      v-if="skeleton"
      class="picker__elements d-flex flex-wrap justify-center"
    >
      <div
        v-for="(color, index) in colors"
        :key="index"
        class="picker__element d-flex flex-wrap align-center"
        :class="{
          'not-available': productIsOnSale && !color.available,
        }"
      >
        <picker-item name="color" :value="`${index}`" skeleton />
      </div>
    </div>
    <div v-else class="picker__elements d-flex flex-wrap justify-center">
      <div
        v-for="color in colorsWithBreaks"
        :key="color.color"
        class="picker__element d-flex flex-wrap align-center"
        :class="{
          break: color.break,
          'picker__element--active': value === color.value,
          'not-available': productIsOnSale && !color.available && !color.break,
        }"
      >
        <picker-item
          v-if="!color.break"
          name="color"
          :value="color.value"
          :label="color.label"
          :active="value === color.value"
          :bg="color.image.url"
          :has-notif="color.isNew || color.isLeavingSoon"
          @input="selectColor(color)"
          @mouseenter="hoverColor(color)"
          @mouseleave="leaveColor(color)"
        />
      </div>
    </div>

    <transition name="fade-from-top">
      <div
        v-if="showIsLeavingSoon || showIsNew"
        class="picker__notif d-none d-md-block text-small text-md-base text-left mt-0-8"
      >
        <p class="mb-0-2 w-600">
          {{
            $i18n.t(`product.color.${showIsNew ? 'new' : 'leaving_soon'}.title`)
          }}
        </p>
        <p v-if="showIsLeavingSoon && daysLeft === -1" class="mb-0">
          {{ $i18n.t('product.color.leaving_soon.ended') }}
        </p>
        <p
          v-else
          class="mb-0"
          :class="{
            'd-none': $i18n.locale === 'en-US' && showIsNew,
          }"
        >
          {{
            $i18n.t(
              `product.color.${showIsNew ? 'new' : 'leaving_soon'}.desc`,
              {
                left:
                  daysLeft !== null
                    ? $i18n.tc(
                        'product.color.leaving_soon.days_left',
                        daysLeft,
                        {
                          count: daysLeft,
                        }
                      )
                    : $i18n.t('product.color.leaving_soon.some_days'),
                product: productDocument.title,
                colour: selectColorLabel,
              }
            )
          }}
        </p>
      </div>
    </transition>
  </picker>
</template>

<script>
import { differenceInDays, isPast } from 'date-fns'
import { toHTML } from '@portabletext/to-html'

import Picker from '@/components/picker/Picker'
import PickerItem from '@/components/picker/PickerItem'
import Icon from '@/components/icons/Icon'

export default {
  name: 'ColorPicker',
  components: {
    Picker,
    PickerItem,
    Icon,
  },
  props: {
    value: { type: String, required: true },
    /**
     * Liste des couleurs
     */
    colors: {
      type: Array,
      required: true,
    },
    /**
     * identifiant du color picker
     */
    id: {
      type: String,
      default: 'colorPicker',
    },
    /**
     * Produit sanity
     */
    productDocument: {
      type: Object,
      default: () => ({}),
    },
    /** Le produit est-il en vente */
    productIsOnSale: {
      type: Boolean,
      default: false,
    },
    /**
     * Affiche le label du color picker
     */
    withLabel: {
      type: Boolean,
      default: false,
    },
    showThird: {
      type: Boolean,
      default: false,
    },
    thirdDimensionName: {
      type: String,
      default: null,
    },
    thirdDimensionLabel: {
      type: String,
      default: null,
    },
    thirdDimension: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Le composant est affiché en squelette de chargement
     */
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    hoveredColor: null,
    hoveredThird: null,
  }),
  computed: {
    selectColorLabel() {
      if (
        this.value &&
        this.colors.some((color) => color.value === this.value)
      ) {
        return this.colors.find((color) => color.value === this.value).label
      }

      return ''
    },
    showIsLeavingSoon() {
      return (
        this.value &&
        this.colors.some(
          (color) => color.value === this.value && color.isLeavingSoon
        )
      )
    },
    showIsNew() {
      return (
        this.value &&
        this.colors.some((color) => color.value === this.value && color.isNew)
      )
    },
    maxPerLine() {
      if (this.colors.length > 8) {
        return Math.ceil(this.colors.length / 2)
      }

      return 8
    },
    numberOfLine() {
      return Math.ceil(this.colors.length / this.maxPerLine)
    },
    colorsWithBreaks() {
      let arr = []

      for (let i = 0; i < this.numberOfLine; i++) {
        arr = arr.concat(
          this.colors.slice(this.maxPerLine * i, this.maxPerLine * (i + 1))
        )

        arr.push({
          color: `break-${i}`,
          break: true,
        })
      }

      return arr
    },
    daysLeft() {
      if (this.showIsLeavingSoon) {
        if (this.productDocument.current_batch_date_end) {
          if (isPast(this.productDocument.current_batch_date_end)) {
            return -1
          }

          return differenceInDays(
            this.productDocument.current_batch_date_end,
            new Date()
          )
        }

        return 0
      }
      return null
    },
    materialDesc() {
      if (this.showThird && this.thirdDimension) {
        return `${this.$t('product.material.popup', {
          label: this.thirdDimensionLabel
            ? this.$t('product.material.label', {
                label: this.thirdDimensionLabel,
              })
            : this.$t('product.material.title'),
          name: this.thirdDimension.label,
          icon: ` <svg aria-hidden="true" focusable="false" class="icon">
                  <use xlink:href="/icons.svg#info"></use>
                </svg>`,
        })}${toHTML(this.thirdDimension.desc)}`
      }

      return ''
    },
  },
  mounted() {
    if (this.showThird && this.$refs.thirdDimension) {
      this.initTooltip()
    }

    this.updateNotifTooltip()
  },
  methods: {
    selectColor(color) {
      this.$emit('input', color.value, color)
      this.updateNotifTooltip()
    },
    hoverColor(color) {
      this.hoveredColor = color.label

      if (this.showThird && color[this.thirdDimensionName]) {
        this.hoveredThird = color[this.thirdDimensionName]
      }
    },
    leaveColor() {
      this.hoveredColor = null
      this.hoveredThird = null
    },
    async updateNotifTooltip() {
      await this.$nextTick()
      if (this.$refs.colorNotif) {
        const tippy = await import('tippy.js' /* webpackChunkName: 'tippy' */)
        await import(
          'tippy.js/animations/scale.css' /* webpackChunkName: 'tippy_animation_scale_css' */
        )
        await import(
          '@/scss/09-libs/_06-tippy.scss' /* webpackChunkName: 'tippy_css' */
        )

        tippy.default(this.$refs.colorNotif, {
          theme: 'dark',
          placement: 'top',
          animation: 'scale',
          arrow: true,
          duration: [300],
          delay: [300, 100],
          allowHTML: true,
          onShow: (instance) => {
            if (this.showIsLeavingSoon && this.daysLeft === -1) {
              instance.setContent(this.$t('product.color.leaving_soon.ended'))
            } else {
              instance.setContent(
                this.$t(
                  `product.color.${
                    this.showIsNew ? 'new' : 'leaving_soon'
                  }.desc`,
                  {
                    left:
                      this.daysLeft !== null
                        ? this.$i18n.tc(
                            'product.color.leaving_soon.days_left',
                            this.daysLeft,
                            {
                              count: this.daysLeft,
                            }
                          )
                        : this.$t('product.color.leaving_soon.some_days'),
                    product: this.productDocument.title,
                    colour: this.selectColorLabel,
                  }
                )
              )
            }
          },
        })
      }
    },
    async initTooltip() {
      const tippy = await import('tippy.js' /* webpackChunkName: 'tippy' */)
      await import(
        'tippy.js/animations/scale.css' /* webpackChunkName: 'tippy_animation_scale_css' */
      )
      await import(
        '@/scss/09-libs/_06-tippy.scss' /* webpackChunkName: 'tippy_css' */
      )

      tippy.default(this.$refs.thirdDimension, {
        theme: 'dark',
        placement: 'top',
        animation: 'scale',
        arrow: true,
        duration: [300],
        delay: [300, 100],
        onShow: (instance) => {
          instance.setContent(this.materialDesc)
        },
        allowHTML: true,
      })
    },
  },
}
</script>

<style lang="scss">
.picker.picker--color {
  margin-bottom: var(--spacing);

  .picker__elements {
    margin: 0 rem(-5px);
  }

  .picker__element {
    margin: 0 rem(5px) rem($spacing * 0.6);
    position: relative;

    &.break {
      display: block;
      height: 0;
      flex: 1 0 100%;
      margin: 0;
    }

    &:not(.picker__element--active).not-available {
      .product--stock & {
        display: none;
      }
    }
  }

  .picker__element__color {
    white-space: nowrap;
    will-change: width opacity;
  }

  .picker-item {
    margin: 0;
  }

  .picker-item__notif {
    top: rem(2px);
  }
}

.picker__label-container {
  gap: 0 calc(var(--spacing) * 0.4);
}

.picker__label--third {
  cursor: help;

  &::before {
    content: '/';
    display: inline-block;
    margin-right: calc(var(--spacing) * 0.2);

    @include mq($from: tablet) {
      display: none;
    }
  }

  .icon {
    opacity: 0.6;
  }
}

.picker--color__label {
  gap: calc(var(--spacing) * 0.4);
}

.picker--color__notif {
  letter-spacing: 0.02em;
}

.picker__notif {
  background-color: var(--secondary-color-5);
  padding: calc(var(--spacing) * 0.8) calc(var(--spacing));

  @include mq($from: tablet) {
    padding: calc(var(--spacing)) calc(var(--spacing) * 1.4);
  }
}
</style>
